import { Loader2, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import EmptyAvatar from '@/assets/icons/empty-avatar.svg?react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import Pagination from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { transformRole } from '@/helpers/role.helper';
import { useGetLeads } from '@/hooks/zee/useGetLeads';

const LEADS_LIMIT_STORAGE_KEY = 'consultant_leads_tab_page_limit';
const LEADS_LIMIT_DEFAULT = 10;

const getStoredLimit = (): number => {
  try {
    const stored = localStorage.getItem(LEADS_LIMIT_STORAGE_KEY);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : LEADS_LIMIT_DEFAULT;
  } catch {
    return LEADS_LIMIT_DEFAULT;
  }
};

// Reasonable sort options for Leads tab
const LEADS_SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'generated', label: 'Generated' },
  { value: 'status', label: 'Status' },
];

interface LeadData {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  leadStatus:
    | 'DRAFT'
    | 'REFERRED'
    | 'NEW'
    | 'INVITED'
    | 'CONVERTED'
    | 'REJECTED'
    | 'ARCHIVED';
  invitationStatus:
    | 'NOT_SENT'
    | 'SENT'
    | 'ACCEPTED'
    | 'REVOKED'
    | 'REFERRED'
    | 'PENDING';
  leadSource: string;
  invitationId?: string;
  clerkInvitationId?: string;
  invitedBy?: {
    id?: string;
    name: string;
    imageUrl?: string;
    email?: string;
    role?: string;
  };
}

const LeadsTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // const leads = result?.data?.leads || [];

  const pageFromUrl = useMemo(
    () => Math.max(1, parseInt(searchParams.get('page') || '1', 10)),
    [searchParams]
  );
  const limitFromUrl = useMemo(() => {
    const urlLimit = searchParams.get('limit');
    if (urlLimit) {
      return Math.max(1, parseInt(urlLimit, 10));
    }
    return getStoredLimit();
  }, [searchParams]);

  const [page, setPage] = useState(pageFromUrl);
  const [limit, setLimit] = useState(limitFromUrl);
  const [sort, setSort] = useState('generated');
  const [searchVal, setSearchVal] = useState('');

  const { result, isLoading, isError, isRefetching } = useGetLeads({
    page,
    limit,
  });
  const leads = result?.data?.leads || [];
  const paginationTotal = result?.data?.total ?? 0;
  const paginationTotalPages = result?.data?.total_pages ?? 1;

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setSearchParams((prev) => {
      prev.set('page', String(newPage));
      return prev;
    });
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
    try {
      localStorage.setItem(LEADS_LIMIT_STORAGE_KEY, String(newLimit));
    } catch {
      // ignore storage errors
    }
    setSearchParams((prev) => {
      prev.set('limit', String(newLimit));
      prev.set('page', '1');
      return prev;
    });
  };

  const transformedLeadsData: LeadData[] = useMemo(() => {
    return leads.map((lead) => ({
      id: lead.id,
      name: lead.name || 'Unknown',
      email: lead.email,
      createdAt: new Date(lead.created_at).toLocaleDateString(),
      leadStatus: lead.status || 'NEW',
      invitationStatus: lead.invitation?.status || 'NOT_SENT',
      leadSource: lead.source || 'MANUAL',
      invitationId: lead.invitation?.id,
      clerkInvitationId: lead.invitation?.clerk_invitation_id,
      invitedBy: lead.inviter
        ? {
            id: lead.inviter.id,
            name: lead.inviter.name || 'Unknown',
            imageUrl: lead.inviter.photo_url,
            email: lead.inviter.email,
            role: lead.inviter.role
              ? transformRole(lead.inviter.role)
              : undefined,
          }
        : undefined,
    }));
  }, [leads]);

  const filteredFields = useMemo(() => {
    const searchLower = searchVal.toLowerCase();
    let filtered = transformedLeadsData.filter(({ name, email, invitedBy }) => {
      return [name, email, invitedBy?.name].some((field) =>
        field?.toLowerCase().includes(searchLower)
      );
    });

    // Enhanced sort logic
    filtered = [...filtered].sort((a, b) => {
      switch (sort) {
        case 'name':
          // Sort pending entries to the end, then alphabetically
          if (a.name === 'Pending' && b.name !== 'Pending') return 1;
          if (a.name !== 'Pending' && b.name === 'Pending') return -1;
          return a.name.localeCompare(b.name);
        case 'email':
          return a.email.localeCompare(b.email);
        case 'status': {
          // Sort by lead status: DRAFT first, then REFERRED, NEW, INVITED, etc.
          const order: Record<LeadData['leadStatus'], number> = {
            DRAFT: 0,
            REFERRED: 1,
            NEW: 2,
            INVITED: 3,
            REJECTED: 4,
            ARCHIVED: 5,
            CONVERTED: 6,
          };
          return (order[a.leadStatus] ?? 99) - (order[b.leadStatus] ?? 99);
        }
        case 'generated':
        default: {
          // Sort by date: newest first
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA;
        }
      }
    });

    return filtered;
  }, [searchVal, transformedLeadsData, sort]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-red-500">Failed to load leads data</p>
          <p className="text-sm text-typo-gray-tertiary">
            Please try again later
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-alpha-dark-5 bg-muted">
      <div className="as-strong as-title-01 flex items-center gap-2 border-b border-border p-4 text-zinc-700">
        Leads
        {isRefetching && <Loader2 className="size-4 animate-spin" />}
      </div>
      <div className="m-4 overflow-hidden rounded-xl border border-alpha-dark-5 bg-white">
        <div className="m-4 flex justify-between">
          <div className="tour-leads-search relative flex items-center">
            <span className="absolute left-3 text-gray-500">
              <Search size={16} />
            </span>
            <Input
              type="text"
              value={searchVal}
              className="pl-8"
              placeholder="Search"
              onChange={(e) => setSearchVal(e.target.value)}
            />
          </div>
          <div className="flex gap-1">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="tour-leads-sort w-[200px] bg-white">
                <span className="text-typo-gray-tertiary">Sort By:</span>
                <SelectValue defaultValue={sort} />
              </SelectTrigger>
              <SelectContent>
                {LEADS_SORT_OPTIONS.map((item) => (
                  <SelectItem value={item.value} key={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="tour-leads-table m-4 overflow-hidden rounded-xl border border-alpha-dark-5 bg-muted">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="as-title-04 tour-prospect-name w-[30%] px-4">
                  Prospect Name
                </TableHead>
                <TableHead className="as-title-04 tour-lead-source w-[15%]">
                  Lead Source
                </TableHead>
                <TableHead className="as-title-04 tour-lead-generated w-[15%]">
                  Generated
                </TableHead>
                <TableHead className="as-title-04 tour-invited-by w-[25%]">
                  Invited By
                </TableHead>
                <TableHead className="as-title-04 tour-lead-status w-[15%]">
                  Brand Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="bg-white text-typo-gray-secondary">
              {filteredFields.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center">
                    <p className="text-typo-gray-tertiary">No leads found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredFields.map((field) => {
                  return (
                    <TableRow key={field.id}>
                      <TableCell className="p-4">
                        <div
                          className="cursor-pointer"
                          onClick={() =>
                            navigate(`/dashboard/lead-profile/${field.id}`)
                          }
                        >
                          <p className="text-sm font-semibold leading-tight text-typo-primary hover:underline">
                            {field.name}
                          </p>
                          <p className="text-xs text-typo-gray-tertiary">
                            {field.email}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <p className="text-sm">{field.leadSource}</p>
                      </TableCell>
                      <TableCell className="py-4">
                        <p className="text-sm">{field.createdAt}</p>
                      </TableCell>
                      <TableCell className="py-4">
                        {field.invitedBy ? (
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-full border">
                              {field.invitedBy.imageUrl ? (
                                <img
                                  src={field.invitedBy.imageUrl}
                                  alt={field.invitedBy.name}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <EmptyAvatar className="!h-full !w-full" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-medium">
                                {field.invitedBy.name || 'Unknown'}
                              </p>
                              {field.invitedBy.role && (
                                <Badge variant="outline" className="text-xs">
                                  {field.invitedBy.role}
                                </Badge>
                              )}
                            </div>
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="py-4">
                        <p className="text-muted-foreground">
                          {(() => {
                            switch (field.leadStatus) {
                              case 'DRAFT':
                                return 'Draft';
                              case 'REFERRED':
                                return 'Sent to Brand';
                              case 'NEW':
                                return 'Viewed by Brand';
                              case 'INVITED':
                                return 'Invite Sent';
                              case 'CONVERTED':
                                return 'Converted';
                              case 'REJECTED':
                                return 'Rejected';
                              case 'ARCHIVED':
                                return 'Archived';
                              default:
                                return 'Unknown';
                            }
                          })()}
                        </p>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        <div className="border-t border-alpha-dark-5 px-4 py-3">
          <Pagination
            page={page}
            totalPages={paginationTotalPages}
            total={paginationTotal}
            limit={limit}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
          />
        </div>
      </div>
    </div>
  );
};

export default LeadsTab;
